import {useMemo} from 'react';
import {useGetProductPricingQuery} from '@/services/api/product-pricing-api';
import {useSelectedZone} from '@/services/geoblocking/hooks/useSelectedZone';
import {useCountryCode} from './useCountryCode';

/**
 * Returns the `distributor` flag based on the current hostname.
 * Mirrors the logic in ppSectionData.getDistributorConfig().
 * @returns {boolean}
 */
function isDistributor() {
  const host = globalThis.location.host;

  if (`${host}/` === '.esri.com.tr/') {
    return true;
  }

  if (host.includes('preview') || host.includes('www.')) {
    return `${host}/` !== 'preview.esri.com' && `${host}/` !== 'www.esri.com';
  }

  // dev and stage environments
  return false;
}

/**
 * Fetches product pricing data for a single product path via RTK Query.
 *
 * The query is skipped until both `productPaths` and a country code cookie
 * (`omniCoutryCode`) are present. `selectedZone` is appended to the payload
 * when available.
 *
 * @param {string|string[]|null|undefined} productPaths - The AEM content-fragment path(s) for the product.
 * @param {string|null|undefined} [countryCodeOverride] - When provided, overrides the resolved country code from cookies and geoblocking.
 * @param {string|null|undefined} [selectedZoneOverride] - When provided, overrides the resolved selected zone from geoblocking.
 * @returns {import('@reduxjs/toolkit/query/react').UseQueryResult} RTK Query result object.
 */
export function useProductPricing(productPaths, countryCodeOverride, selectedZoneOverride) {
  const resolvedCountryCode = useCountryCode();
  const countryCode = countryCodeOverride || resolvedCountryCode;
  const resolvedSelectedZone = useSelectedZone();
  const selectedZone = selectedZoneOverride || resolvedSelectedZone;

  const payload = useMemo(() => {
    if (!productPaths || !countryCode) return null;

    const paths = [productPaths].flat();
    const body = {
      verificationNeeded: 'false',
      country_code: countryCode,
      data: paths.map((path) => ({
        contentFragment: path,
        isBundle: false,
      })),
      distributor: isDistributor(),
    };

    if (selectedZone) {
      body.selectedZone = selectedZone;
    }

    return body;
  }, [productPaths, countryCode, selectedZone]);

  return useGetProductPricingQuery(payload, {skip: !payload});
}
